import { useEffect, useRef } from 'react';

const NEXT_KEYS = ['ArrowRight', ' ', 'Spacebar'];
const PREV_KEYS = ['ArrowLeft'];

// Somewhere the key already means something: typing in a text box, picking
// from a select, editing rich text.
function isEditable(target) {
    if (!target || typeof target.closest !== 'function') return false;
    if (target.isContentEditable) return true;
    return !!target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])');
}

// Space presses a focused button, link or similar control, so leave it to them.
function isPressable(target) {
    if (!target || typeof target.closest !== 'function') return false;
    return !!target.closest(
        'button, a[href], [role="button"], [role="link"], [role="checkbox"], [role="switch"], [role="menuitem"], [role="tab"], [role="option"]'
    );
}

/**
 * PowerPoint-style keyboard controls for the presenter: Right arrow and Space
 * go to the next slide, Left arrow to the previous one.
 *
 * Keys are ignored while focus is in a text field (chat, the room name, ...),
 * while a modifier is held (so browser shortcuts like Alt+Left still work),
 * and when something else already handled the key. Space is also left alone
 * when a button or link has focus, so it still presses it.
 *
 * Staying within the first and last slide is the callbacks' job - the room's
 * prevSlide/nextSlide already stop at the ends.
 *
 * @param onPrev - go to the previous slide
 * @param onNext - go to the next slide
 * @param enabled - whether this user may change the slide at all
 */
export default function useSlideKeyboardNav({ onPrev, onNext, enabled = true }) {
    // The room rebuilds its prev/next callbacks every render; read the latest
    // through a ref so the listener is attached once, not once per render.
    const handlersRef = useRef({ onPrev, onNext });
    handlersRef.current = { onPrev, onNext };

    useEffect(() => {
        if (!enabled) return undefined;

        const handleKeydown = (e) => {
            if (e.defaultPrevented) return;
            if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

            const isNext = NEXT_KEYS.includes(e.key);
            const isPrev = PREV_KEYS.includes(e.key);
            if (!isNext && !isPrev) return;

            if (isEditable(e.target)) return;
            const isArrow = e.key === 'ArrowRight' || e.key === 'ArrowLeft';
            if (!isArrow && isPressable(e.target)) return;

            // Stops Space and the arrows scrolling the page as well.
            e.preventDefault();
            const { onPrev: prev, onNext: next } = handlersRef.current;
            const go = isNext ? next : prev;
            if (go) go();
        };

        window.addEventListener('keydown', handleKeydown);
        return () => window.removeEventListener('keydown', handleKeydown);
    }, [enabled]);
}
